import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Header from './Header';
import Footer from './Footer';
import { Button } from './ui/button';
import { 
  Sparkles, Clock, CheckCircle, ArrowRight
} from 'lucide-react';

const MarkdownBlogLayout = ({ title, category, readTime, date, author, content }) => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-50 via-white to-white"></div>
        <div className="container mx-auto max-w-4xl relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 text-gray-700 text-sm font-medium border border-gray-200 mb-8">
            <Sparkles className="w-4 h-4" />
            <span>{category}</span>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold mb-8 text-gray-900 leading-tight tracking-tight">
            {title}
          </h1>
          
          <div className="flex items-center justify-center space-x-4 text-sm text-gray-500 font-medium">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-700 text-xs font-bold">
                {author?.[0] || 'A'}
              </div>
              <span>{author || 'Asha Team'}</span>
            </div>
            <span>•</span>
            <span>{date || 'Today'}</span>
            <span>•</span>
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              <span>{readTime}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <article className="pb-24 px-6">
        <div className="container mx-auto max-w-3xl">
          <ReactMarkdown 
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({node, ...props}) => <h1 className="hidden" {...props} />, // Title is already in hero
              h2: ({node, ...props}) => <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6" {...props} />,
              h3: ({node, ...props}) => <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4" {...props} />,
              h4: ({node, ...props}) => <h4 className="text-lg font-bold text-gray-900 mt-6 mb-3" {...props} />,
              p: ({node, ...props}) => <p className="text-gray-600 leading-relaxed mb-6 text-lg" {...props} />,
              ul: ({node, ...props}) => <ul className="space-y-3 mb-8" {...props} />,
              ol: ({node, ...props}) => <ol className="list-decimal list-inside space-y-3 mb-8 text-gray-700" {...props} />,
              li: ({node, ...props}) => (
                <li className="flex items-start gap-2 text-gray-700">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-600 flex-shrink-0" />
                  <span className="flex-1">{props.children}</span>
                </li>
              ),
              blockquote: ({node, ...props}) => (
                <blockquote className="bg-blue-50 p-6 rounded-2xl border border-blue-100 italic text-gray-700 mb-8 relative">
                   <div className="absolute top-0 left-0 w-1 h-full bg-blue-500 rounded-l-2xl opacity-20"></div>
                   {props.children}
                </blockquote>
              ),
              a: ({node, ...props}) => <a className="text-blue-600 hover:text-blue-700 underline decoration-blue-200 hover:decoration-blue-500 transition-all" {...props} />,
              strong: ({node, ...props}) => <strong className="text-gray-900 font-bold" {...props} />,
              table: ({node, ...props}) => <div className="overflow-x-auto mb-8"><table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg" {...props} /></div>,
              thead: ({node, ...props}) => <thead className="bg-gray-50" {...props} />,
              tbody: ({node, ...props}) => <tbody className="bg-white divide-y divide-gray-200" {...props} />,
              tr: ({node, ...props}) => <tr className="" {...props} />,
              th: ({node, ...props}) => <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" {...props} />,
              td: ({node, ...props}) => <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600" {...props} />,
              hr: ({node, ...props}) => <hr className="my-12 border-gray-100" {...props} />,
            }}
          >
            {content}
          </ReactMarkdown>
        </div>
      </article>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gray-900 text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-8">
            Ready to Automate Your Practice?
          </h2>
          <p className="text-xl text-gray-300 mb-12 max-w-2xl mx-auto">
            Join the only platform that offers a complete team of AI agents to drive your practice's growth.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
             <a href="https://calendly.com/asha-ai/30min" target="_blank" rel="noopener noreferrer">
              <Button className="bg-white text-gray-900 hover:bg-gray-100 px-8 py-6 rounded-xl text-lg font-bold shadow-lg transition-all duration-300 w-full sm:w-auto">
                Schedule Follow-up Call
              </Button>
            </a>
            <a href="/book-demo">
              <Button className="bg-blue-600 hover:bg-blue-500 text-white border border-blue-500 px-8 py-6 rounded-xl text-lg font-bold shadow-lg transition-all duration-300 w-full sm:w-auto">
                Book a Demo
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MarkdownBlogLayout;
